import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true, // Always send cookies for session-based auth
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear token and redirect if on admin pages
      if (window.location.pathname.startsWith('/admin')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
  logout: () => api.post('/auth/logout'),
};

// Articles APIs
export const articlesAPI = {
  getAll: (params) => api.get('/articles', { params }),
  getOne: (id) => api.get(`/articles/${id}`),
  create: (data) => api.post('/articles', data),
  update: (id, data) => api.put(`/articles/${id}`, data),
  delete: (id) => api.delete(`/articles/${id}`),
};

// Initiatives APIs
export const initiativesAPI = {
  getAll: () => api.get('/initiatives'),
  getOne: (id) => api.get(`/initiatives/${id}`),
  create: (data) => api.post('/initiatives', data),
  update: (id, data) => api.put(`/initiatives/${id}`, data),
  delete: (id) => api.delete(`/initiatives/${id}`),
};

// Media APIs
export const mediaAPI = {
  getAll: () => api.get('/media'),
  getOne: (id) => api.get(`/media/${id}`),
  create: (data) => api.post('/media', data),
  update: (id, data) => api.put(`/media/${id}`, data),
  delete: (id) => api.delete(`/media/${id}`),
};

// Contact APIs
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getAll: () => api.get('/contact'),
  markRead: (id) => api.put(`/contact/${id}/read`),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  // User management
  getUsers: () => api.get('/admin/users'),
  updateUserRole: (id, role) => api.put(`/admin/users/${id}/role`, { role }),
  suspendUser: (id) => api.put(`/admin/users/${id}/suspend`),
  unsuspendUser: (id) => api.put(`/admin/users/${id}/unsuspend`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  // Article workflow
  getPendingArticles: () => api.get('/admin/articles/pending'),
  previewArticle: (id) => api.get(`/admin/articles/${id}/preview`),
  approveArticle: (id) => api.post(`/admin/articles/${id}/approve`),
  rejectArticle: (id, reason) => api.post(`/admin/articles/${id}/reject`, { reason }),
};

// Author APIs
export const authorAPI = {
  getArticles: () => api.get('/author/articles'),
  createArticle: (data) => api.post('/author/articles', data),
  updateArticle: (id, data) => api.put(`/author/articles/${id}`, data),
  submitForReview: (id) => api.post(`/author/articles/${id}/submit`),
};

// Comments APIs
export const commentsAPI = {
  getComments: (contentType, contentId) => api.get(`/comments/${contentType}/${contentId}`),
  createComment: (contentType, contentId, text) => api.post(`/comments/${contentType}/${contentId}`, { text }),
  hideComment: (id) => api.put(`/comments/${id}/hide`),
  unhideComment: (id) => api.put(`/comments/${id}/unhide`),
  getDashboardComments: () => api.get('/dashboard/comments'),
};

// Notifications APIs
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
};

// Image upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export default api;
